'use client';

import { useCallback, useState } from 'react';

import {
  IArquivoParaOVesto,
  IExportarParaVestoPayload,
  IPendenciaDoVesto,
} from '@/domain/ops/interfaces/exportacaoVesto';
import { exportacaoVestoRepository } from '@/domain/ops/repositories/exportacaoVestoRepository';
import { logger } from '@/infrastructure/persistence/managers/LoggerManager';

/** Enquanto a pendência não chegou: zero não mostra o aviso, que é o estado seguro. */
const SEM_PENDENCIA: IPendenciaDoVesto = { comDataDiferente: 0, ultimaEm: null, ultimaPor: '' };

/**
 * A devolução das datas para o VESTO, do ponto de vista da tela.
 *
 * Estado LOCAL e não store: o aviso aparece no Início e na Carteira, que são rotas
 * diferentes e nunca estão montadas juntas, então não há dois componentes disputando o
 * mesmo carregamento. Guardar isso num store global só adicionaria um dado para invalidar.
 *
 * A falha do carregamento deixa `comDataDiferente` em zero DE PROPÓSITO: o aviso some em
 * vez de mostrar um número que a tela não conseguiu ler. Dizer "0 OPs diferentes" quando a
 * leitura falhou seria pior que não dizer nada.
 */
export function useExportacaoVesto() {
  const [pendencia, setPendencia] = useState<IPendenciaDoVesto>(SEM_PENDENCIA);
  const [exportando, setExportando] = useState(false);

  const carregarPendencia = useCallback(async (): Promise<void> => {
    try {
      setPendencia(await exportacaoVestoRepository.pendencia());
    } catch (error) {
      logger.error('[useExportacaoVesto] pendencia failed', error);
      setPendencia(SEM_PENDENCIA);
    }
  }, []);

  /**
   * Gera o arquivo e devolve o conteúdo para a tela salvar. LANÇA no erro: é a tela que
   * mostra a mensagem da API e mantém o diálogo aberto para a pessoa tentar de novo.
   *
   * No sucesso recarrega a pendência, porque é exatamente o que a exportação muda: o aviso
   * tem de zerar sem ninguém recarregar a página.
   */
  const exportar = useCallback(
    async (payload: IExportarParaVestoPayload): Promise<IArquivoParaOVesto> => {
      setExportando(true);
      try {
        const arquivo = await exportacaoVestoRepository.exportar(payload);
        await carregarPendencia();
        return arquivo;
      } finally {
        setExportando(false);
      }
    },
    [carregarPendencia],
  );

  return { pendencia, exportando, carregarPendencia, exportar };
}
